import { useMemo } from "react";
import {
  ChevronDown,
  ChevronRight,
  CheckCheck,
  ArrowRight,
  Flag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PrelimCategoryGroup } from "../PrelimCategoryGroup";
import { DOMAINS_META } from "@/utils/maturity-colors";
import type {
  ElementType,
  QuestionnaireItem,
  CyFunActivity,
} from "@/types/maturity";
import { SECTION_ICONS, SECTION_QUESTIONS } from "@/types/maturity";

type PrelimSectionProps = {
  sectionType: ElementType;
  items: QuestionnaireItem[];
  responses: Record<string, boolean>;
  enabled: boolean;
  expanded: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onToggleExpanded: () => void;
  onCheckboxChange: (itemIndex: string, checked: boolean) => void;
  onToggleAll: (itemIndexes: string[], checked: boolean) => void;
  onSelectAll: () => void;
  onNextSection: () => void;
  isLastSection: boolean;
  activities: CyFunActivity[];
};

function PrelimSection({
  sectionType,
  items,
  responses,
  enabled,
  expanded,
  onToggleEnabled,
  onToggleExpanded,
  onCheckboxChange,
  onToggleAll,
  onSelectAll,
  onNextSection,
  isLastSection,
  activities,
}: PrelimSectionProps) {
  const groupedByDomain = useMemo(() => {
    const groups: Record<string, QuestionnaireItem[]> = {};
    for (const item of items) {
      const domainCode = item.linked_activity_codes[0] ?? "OTHER";
      if (!groups[domainCode]) groups[domainCode] = [];
      groups[domainCode].push(item);
    }

    const domainOrder = DOMAINS_META.map((d) => d.id);
    return Object.entries(groups).sort(([a], [b]) => {
      const aIdx = domainOrder.indexOf(a);
      const bIdx = domainOrder.indexOf(b);
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });
  }, [items]);

  const checkedCount = useMemo(
    () =>
      items.filter((item) => responses[String(item.item_index)] === true)
        .length,
    [items, responses]
  );

  const icon = SECTION_ICONS[sectionType];
  const question = SECTION_QUESTIONS[sectionType];

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex items-center gap-3 flex-1 text-left"
        >
          <span className="text-xl">{icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">
              {sectionType}
            </h3>
            <p className="text-xs text-slate-500 truncate">{question}</p>
          </div>
          <span className="text-xs text-slate-400 mr-2">
            {checkedCount}/{items.length}
          </span>
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
          )}
        </button>
      </div>

      {expanded && (
        <TooltipProvider delayDuration={200}>
          <div className="px-5 pb-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">
                {checkedCount} din {items.length} selectate
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 h-7"
                onClick={onSelectAll}
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Selectează tot
              </Button>
            </div>

            <div className="space-y-3">
              {groupedByDomain.map(([domainCode, domainItems]) => {
                const domainMeta = DOMAINS_META.find(
                  (d) => d.id === domainCode
                );
                const activity = activities.find((a) => a.code === domainCode);
                const domainName = activity?.name ?? domainCode;

                return (
                  <PrelimCategoryGroup
                    key={domainCode}
                    domainCode={domainCode}
                    domainName={domainName}
                    domainColor={domainMeta?.color ?? "#6B7280"}
                    domainBg={domainMeta?.bg ?? "#F9FAFB"}
                    items={domainItems}
                    responses={responses}
                    onCheckboxChange={onCheckboxChange}
                    onToggleAll={onToggleAll}
                  />
                );
              })}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={onNextSection}
              >
                {isLastSection ? (
                  <>
                    <Flag className="w-3.5 h-3.5" />
                    Finalizează evaluarea
                  </>
                ) : (
                  <>
                    Mergi la secțiunea următoare
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}

export default PrelimSection;
